import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', slug: '' });
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tenants');
      setTenants(response.data || []);
    } catch (error) {
      toast.error('Error cargando empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tenants', formData);
      toast.success('Empresa creada exitosamente');
      setDialogOpen(false);
      setFormData({ name: '', slug: '' });
      fetchTenants();
    } catch (error) {
      toast.error('Error creando empresa');
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return tenants.filter((tenant) => {
      const matchesQuery =
        !normalized ||
        tenant.name?.toLowerCase().includes(normalized) ||
        tenant.slug?.toLowerCase().includes(normalized);
      const matchesStatus =
        statusFilter === 'all' ? true : tenant.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [tenants, query, statusFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const direction = sortDir === 'asc' ? 1 : -1;
      const aValue = (a[sortKey] || '').toString().toLowerCase();
      const bValue = (b[sortKey] || '').toString().toLowerCase();
      if (aValue < bValue) return -1 * direction;
      if (aValue > bValue) return 1 * direction;
      return 0;
    });
    return copy;
  }, [filtered, sortDir, sortKey]);

  const allSelected = sorted.length > 0 && sorted.every((t) => selected[t.id]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    sorted.forEach((tenant) => {
      next[tenant.id] = true;
    });
    setSelected(next);
  };

  const toggleRow = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleBulkCopy = () => {
    const ids = Object.keys(selected).filter((id) => selected[id]);
    if (!ids.length) {
      toast.error('Selecciona al menos una empresa');
      return;
    }
    navigator.clipboard?.writeText(ids.join('\n'));
    toast.success('IDs copiados');
  };

  const handleBulkExport = () => {
    const rows = sorted.filter((t) => selected[t.id]);
    if (!rows.length) {
      toast.error('Selecciona al menos una empresa');
      return;
    }
    const header = ['name', 'slug', 'status', 'subscription_status'];
    const csv = [
      header.join(','),
      ...rows.map((t) =>
        [t.name, t.slug, t.status, t.subscription_status || ''].map((v) => `"${v || ''}"`).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'empresas.csv';
    link.click();
  };

  return (
    <div data-testid="tenants-page">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Empresas</h1>
          <p className="text-muted-foreground">Gestiona los clientes/empresas</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-tenant-button">
              <Plus className="w-4 h-4" />
              Crear Empresa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nueva Empresa</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="tenant-name-input"
                />
              </div>
              <div>
                <Label>Slug (identificador)</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  data-testid="tenant-slug-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-tenant">Crear</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por nombre o slug"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="all">Todos</option>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleBulkCopy}>Copiar IDs</Button>
            <Button variant="outline" onClick={handleBulkExport}>Exportar CSV</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('name')}>
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('slug')}>
                    Slug
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('status')}>
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium">Suscripcion</th>
                  <th className="px-6 py-3 text-left text-sm font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="px-6 py-4 text-center">Cargando...</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-4 text-center">No hay empresas</td></tr>
                ) : (
                  sorted.map((tenant) => (
                    <tr key={tenant.id} className="border-t hover:bg-muted/50" data-testid={`tenant-row-${tenant.id}`}>
                      <td className="px-4 py-4">
                        <input type="checkbox" checked={Boolean(selected[tenant.id])} onChange={() => toggleRow(tenant.id)} />
                      </td>
                      <td className="px-6 py-4">{tenant.name}</td>
                      <td className="px-6 py-4 text-muted-foreground">{tenant.slug}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs ${tenant.status === 'active' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-gray-500/15 text-gray-700'}`}>
                          {tenant.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {tenant.subscription_status ? (
                          <span className="text-sm text-muted-foreground">{tenant.subscription_status}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard?.writeText(tenant.id);
                            toast.success('ID copiado');
                          }}
                        >
                          Copiar ID
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Tenants;
